/**
 * Shared progress bar component. Used everywhere: dashboard, classroom,
 * donation page, parent detail. One implementation, consistent height
 * and styling.
 *
 * Props:
 *   label     — left-aligned label text (e.g. "Fundraising goal")
 *   detail    — right-aligned detail text (e.g. "$915 / $5,000")
 *   pct       — 0–100 fill percentage
 *   footnote  — optional small text below the bar (e.g. "18% of goal")
 *   barColor  — Tailwind bg class for the fill (default: "bg-primary")
 *   footnoteColor — Tailwind text class for footnote (default: "text-primary")
 *   size      — "sm" (h-2) | "md" (h-2.5, default) | "lg" (h-3.5)
 */
export default function ProgressBar({
  label,
  detail,
  pct,
  footnote,
  barColor = "bg-primary",
  footnoteColor = "text-primary",
  size = "md",
}) {
  const heights = { sm: "h-2", md: "h-2.5", lg: "h-3.5" };
  const h = heights[size] || heights.md;

  return (
    <div className="space-y-1.5">
      {(label || detail) && (
        <div className="flex items-baseline justify-between text-sm">
          {label && <span className="text-muted-foreground">{label}</span>}
          {detail && <span className="font-tabular text-muted-foreground">{detail}</span>}
        </div>
      )}
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct || 0)}
        aria-label={typeof label === "string" ? label : undefined}
        className={`${h} bg-secondary rounded-full overflow-hidden border border-border`}
      >
        <div
          className={`h-full ${barColor} rounded-full transition-[width] duration-500 motion-reduce:transition-none`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {footnote && (
        <p className={`text-xs font-medium ${footnoteColor}`}>{footnote}</p>
      )}
    </div>
  );
}
